import React from "react";
import { Link } from "react-router-dom";

export default function TermsPage() {
  return (
    <div className="px-4 sm:px-6 py-12 max-w-3xl mx-auto">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#0A0A0A] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
        LEGAL · B2B TERMS
      </div>
      <h1 className="text-4xl sm:text-5xl tracking-tight mb-4" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
        Terms of sale
      </h1>
      <p className="text-sm text-[#525252] mb-10" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
        Effective February 2026 · Version 1.0 · Governing law: Lithuania
      </p>

      <div className="prose prose-sm max-w-none text-[#0A0A0A]" style={{ fontFamily: "'IBM Plex Sans', sans-serif", lineHeight: 1.65 }}>
        <Section n="1" title="Who we are">
          SolarBridge is a European solar-industry lead-generation service operated by a private
          individual seller based in Lithuania. Contact: <a className="text-[#0047FF] underline underline" href="mailto:contact@solarbridge.eu">contact@solarbridge.eu</a>.
          These terms govern the sale of digital lead lists ("Products") to business buyers ("Buyer") of
          any nationality inside or outside the European Union.
        </Section>

        <Section n="2" title="Business-to-business only">
          Products are sold strictly to businesses, sole traders, associations, distributors, and other
          professional buyers using the lists in the course of their trade. This is not a B2C
          service. By purchasing, Buyer confirms it is acting in a business capacity, and the EU
          consumer right-of-withdrawal (Directive 2011/83/EU, Art. 9) does not apply.
        </Section>

        <Section n="3" title="Payment terms">
          Invoices are due within 14 days of issue and payable by SEPA wire transfer to the IBAN listed
          on the invoice. Payments must include the invoice number as the wire reference so that they
          can be automatically matched. Late payments accrue interest at the statutory Lithuanian rate
          applicable to B2B contracts.
        </Section>

        <Section n="4" title="Delivery">
          The Product is a CSV file (or equivalent format on request) delivered by email to the
          address specified on the invoice within 24 hours of the wire transfer being received and
          matched. SolarBridge is not responsible for delivery failures caused by Buyer's mail
          filters, wrong email address, or unreachable inbox.
        </Section>

        <Section n="5" title="No refunds after delivery">
          Because the Product is a fully-delivered digital good, <b>the sale is final once the Product
          has been emailed to Buyer</b>. No refunds, chargebacks, or partial credits are issued after
          delivery, regardless of the reason. Buyer waives any right to dispute the charge with their
          bank on the basis of "digital delivery" or similar grounds.
        </Section>

        <Section n="6" title="Data quality guarantee">
          SolarBridge compiles Products from publicly available European business directories,
          company websites, and other public sources at the time of compilation. Contact information
          may become outdated between compilation and delivery. If more than <b>10%</b> of the entries
          on a delivered list are shown, within <b>14 days of delivery</b>, to have invalid email
          addresses or non-working phone numbers, SolarBridge will replace the affected entries free
          of charge, on written request to <a className="text-[#0047FF] underline underline" href="mailto:contact@solarbridge.eu">contact@solarbridge.eu</a>.
          No cash refund is issued in place of replacement.
        </Section>

        <Section n="7" title="Permitted use and restrictions">
          Buyer is granted a non-transferable, non-exclusive licence to use the Product for its own
          business outreach and internal analysis. The following are expressly prohibited:
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Reselling, redistributing, sublicensing, or republishing the Product, in whole or in part.</li>
            <li>Uploading the Product to public repositories, marketplaces, or shared databases.</li>
            <li>Sharing the Product with third parties who have not purchased their own licence.</li>
            <li>Using the Product for unsolicited bulk communications that violate the recipient country's
              anti-spam laws (GDPR, ePrivacy Directive, CAN-SPAM equivalents).</li>
          </ul>
        </Section>

        <Section n="8" title="Compliance with data-protection law">
          The Products contain B2B contact information of natural persons acting in their
          professional capacity, gathered from public sources under the "legitimate interest"
          lawful basis (GDPR Art. 6(1)(f)). Buyer becomes an independent data controller upon
          delivery and is solely responsible for its own compliance with GDPR, ePrivacy, and any
          national implementations, including honouring opt-outs, providing privacy notices at first
          contact, and offering unsubscribe mechanisms in any outreach it sends.
        </Section>

        <Section n="9" title="Limitation of liability">
          SolarBridge's total aggregate liability arising out of or in connection with any Product
          shall not exceed the amount paid by Buyer for that specific Product in the twelve months
          preceding the claim. In no event shall SolarBridge be liable for lost profits, lost
          revenue, or indirect, consequential, or punitive damages.
        </Section>

        <Section n="10" title="Governing law and disputes">
          These terms are governed by the laws of the Republic of Lithuania. Any dispute arising
          shall be resolved by good-faith negotiation between the parties within 30 days; failing
          that, by the competent courts of Vilnius, Lithuania. Nothing in this clause prevents
          either party from seeking injunctive relief in a court of competent jurisdiction.
        </Section>

        <Section n="11" title="Changes to these terms">
          SolarBridge may update these terms at any time. The version in effect at the moment of
          invoice issue governs that specific sale. Historical versions are archived on request.
        </Section>
      </div>

      <div className="mt-16 pt-8 border-t border-[#0A0A0A]/10 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-[#525252]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          Questions? <a className="text-[#0047FF] underline" href="mailto:contact@solarbridge.eu">contact@solarbridge.eu</a>
        </div>
        <Link
          to="/products"
          data-testid="terms-back-to-products"
          className="bg-[#3E8B3A] text-[#0A0A0A] px-5 py-3 text-[10px] tracking-[0.2em] uppercase rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
          style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
        >
          ← BACK TO PRODUCTS
        </Link>
      </div>
    </div>
  );
}

const Section = ({ n, title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg mb-2 flex items-baseline gap-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
      <span className="text-[#525252] text-sm tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>{n}.</span>
      {title}
    </h2>
    <div className="text-sm">{children}</div>
  </section>
);
